import { reset, submit } from "redux-form";

import {
  Box,
  Button,
  Chip,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CreditCardIcon,
  InfoIcon,
  LaptopIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { fetchCurrentUser } from "@js/apps/auth/actions";
import { useGetWithdrawalMethodsQuery } from "@js/apps/withdrawal/api/api";
import {
  closeAddWithdrawalMethodOTPModal,
  openAddWithdrawalMethodOTPModal,
} from "@js/apps/withdrawal/components/add-withdrawal-method/withdrawal-method-otp-modal-instance";
import { RouterLink } from "@js/components/link";
import { Modal } from "@js/components/modal";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import type { WithdrawalFormRequirements } from "@js/types/withdrawals";

import type { CreateTransferWiseWithdrawalMethod } from "../../actions";
import {
  createStripeWithdrawalMethod,
  createTransferWiseWithdrawalMethod,
  fetchCurrencyRequirementsClear,
  resendConfirmAddWithdrawalMethod,
} from "../../actions";
import {
  ADD_STRIPE_WITHDRAWAL_METHOD_FORM_ID,
  AddStripWithdrawalMethodForm,
} from "../../forms/add-stripe-withdrawal-method";
import AddTransferWiseWithdrawalForm, {
  ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID,
} from "../../forms/add-transferwise-withdrawal";

import style from "./style.module.scss";

export const AddWithdrawalMethodModal = Modal("add-withdrawal-method", {
  keepOnBackdropClick: true,
  closeButton: false,
});

type AddWithdrawalMethodProps = {
  choosenWithdrawalMethod: EnumType<typeof ENUMS.WithdrawalContentType>;
};

export const openAddWithdrawalMethodModal = (arg: AddWithdrawalMethodProps) => {
  AddWithdrawalMethodModal.open({
    children: <AddWithdrawalMethodModalContent {...arg} />,
    className: style.addWithdrawalMethodModal,
    padding: false,
    keepOnBackdropClick: true,
  });
};

export const AddWithdrawalMethodModalContent = ({
  choosenWithdrawalMethod,
}: AddWithdrawalMethodProps) => {
  const dispatch = useAppDispatch();
  const creatingWithdrawalMethod = useAppSelector(
    (state) => state.withdrawal.creatingWithdrawalMethod,
  );
  const formRequirements = useAppSelector(
    (state) => state.withdrawal.formRequirements,
  );

  const close = () => {
    dispatch(fetchCurrencyRequirementsClear());
    dispatch(fetchCurrentUser()).catch(() => null);
    AddWithdrawalMethodModal.close();
    closeAddWithdrawalMethodOTPModal();
  };

  return (
    <>
      {choosenWithdrawalMethod ===
      ENUMS.WithdrawalContentType.TransferWiseWithdrawalMethod ? (
        <TransferWiseMethodContent
          creatingWithdrawalMethod={creatingWithdrawalMethod}
          formRequirements={formRequirements}
          close={close}
        />
      ) : (
        <StripeMethodContent
          creatingWithdrawalMethod={creatingWithdrawalMethod}
          close={close}
        />
      )}
    </>
  );
};

type TransferWiseMethodContentProps = {
  close: () => void;
  formRequirements: WithdrawalFormRequirements;
  creatingWithdrawalMethod: boolean;
};

const TransferWiseMethodContent = ({
  formRequirements,
  close,
  creatingWithdrawalMethod,
}: TransferWiseMethodContentProps) => {
  const dispatch = useAppDispatch();
  const { refetch: refetchWithdrawalMethods } = useGetWithdrawalMethodsQuery();

  const createTransferWiseWithdrawal = async (
    withdrawalMethod: CreateTransferWiseWithdrawalMethod,
  ) => {
    return dispatch(createTransferWiseWithdrawalMethod(withdrawalMethod)).then(
      () => refetchWithdrawalMethods(),
    );
  };

  const onSubmit = () => {
    return dispatch(submit(ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID));
  };

  const handleSubmit = (data: CreateTransferWiseWithdrawalMethod) => {
    return createTransferWiseWithdrawal(data)
      .then((res) => {
        dispatch(reset(ADD_TRANSFERWISE_WITHDRAWAL_FORM_ID));
        return res;
      })
      .then(close)
      .catch((exception) => {
        const isCodeProvided = !!data.code;
        const isCodeRequired =
          !!exception.errors &&
          (!!exception.errors.code_is_required || !!exception.errors.code);
        if (!isCodeProvided && isCodeRequired) {
          openAddWithdrawalMethodOTPModal();
          return;
        } else if (!isCodeRequired) {
          closeAddWithdrawalMethodOTPModal();
        }

        throw exception;
      });
  };

  return (
    <Stack sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          width: "56px",
          height: "56px",
          boxShadow: "0px 4px 12px 0px #5D19741A",
          background: "#E9D7FF",
          borderRadius: "14px",
          mb: 2,
        }}
      >
        <CreditCardIcon
          sx={{ color: "var(--dark-violet)", fontSize: "26px" }}
        />
      </Box>

      <Chip
        color="violet"
        sx={{
          alignSelf: "start",
          py: "2px !important",
          px: "9px !important",
          background: "#5D197414 !important",
          mb: 1.25,
        }}
        label={
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <LaptopIcon
              sx={{
                fontSize: "14px",
                color: "var(--dark-violet)",
                mb: "2px",
              }}
            />

            <Typography
              component="span"
              fontWeight={500}
              sx={{
                fontSize: "11px !important",
                letterSpacing: "1.2px !important",
                textTransform: "uppercase",
                color: "var(--dark-violet)",
              }}
            >
              Add banking information
            </Typography>
          </Stack>
        }
      />
      <Typography
        component="h3"
        variant="title"
        fontWeight={500}
        size="small"
        sx={{
          mb: 1.25,
          fontSize: "30px !important",
          lineHeight: "40px !important",
        }}
      >
        Tell us where to send your payments.
      </Typography>
      <Typography
        component="p"
        size="small"
        color="grey-1"
        sx={{ mb: 4, maxWidth: "450px" }}
      >
        We pay you directly when you complete contracts. You can apply to jobs
        without this, but you'll need it before any payout can be released.
      </Typography>
      <div className={style.addWithdrawalMethodCard}>
        <AddTransferWiseWithdrawalForm
          formRequirements={formRequirements}
          onSubmit={handleSubmit}
        />
      </div>
      <Stack
        direction="row"
        sx={{
          mt: 3,
          gap: 1.5,
          p: 2,
          border: "1px solid #5D197414",
          background: "#F9F6FF",
          borderRadius: 2.5,
          mb: 3.5,
        }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: "28px",
            height: "28px",
            background: "#5D19741A",
            borderRadius: "7px",
            flexShrink: 0,
          }}
        >
          <InfoIcon sx={{ color: "var(--dark-violet)", fontSize: "14px" }} />
        </Box>
        <Stack>
          <Typography component="p" color="grey-1" size="small">
            <Typography
              component="span"
              fontWeight={500}
              color="primary"
              size="small"
            >
              Why we ask.
            </Typography>{" "}
            Braintrust pays via Wise and Stripe. If your bank is in a country we
            can't pay into, you'll only see jobs where the client pays you
            directly.
          </Typography>
          <Typography
            component="a"
            variant="link"
            RouterLink={RouterLink}
            size="small"
            color="dark-violet"
            fontWeight={500}
            href="https://support.usebraintrust.com/hc/en-us/articles/41239692662807-Where-does-Braintrust-send-payments"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
              <span>View supported Countries</span>
              <ArrowRightIcon
                sx={{ fontSize: "inherit", color: "var(--dark-violet)" }}
              />
            </Stack>
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ gap: 2, justifyContent: "space-between" }}>
        <Button
          variant="transparent"
          color="secondary"
          shape="squared"
          onClick={close}
          size="x-small"
          startIcon={<ArrowLeftIcon />}
        >
          Cancel
        </Button>
        <Button
          variant="dark-violet"
          color="secondary"
          shape="squared"
          disabled={creatingWithdrawalMethod}
          disabledType="no-change"
          onClick={onSubmit}
          endIcon={
            creatingWithdrawalMethod ? <Loader size={20} /> : <CheckIcon />
          }
          size="medium"
          sx={{
            svg: { color: "var(--white)" },
            boxShadow: "0px 4px 12px 0px #5D197440",
          }}
        >
          Save banking
        </Button>
      </Stack>
    </Stack>
  );
};

type StripeMethodContentProps = {
  close: () => void;
  creatingWithdrawalMethod: boolean;
};

const StripeMethodContent = ({
  close,
  creatingWithdrawalMethod,
}: StripeMethodContentProps) => {
  const dispatch = useAppDispatch();
  const { refetch: refetchWithdrawalMethods } = useGetWithdrawalMethodsQuery();
  const showResendEmail = useAppSelector(
    (state) => state.withdrawal.resendConfirmationEmail,
  );
  const forms = useAppSelector((state) => state.form);
  const isForm = !!forms["create-stripe-withdrawal-method"];

  const createStripeWithdrawal = async () => {
    return dispatch(createStripeWithdrawalMethod()).then(() => {
      refetchWithdrawalMethods().catch(() => null);
    });
  };

  const onSubmit = () => {
    return dispatch(submit(ADD_STRIPE_WITHDRAWAL_METHOD_FORM_ID));
  };

  const handleSubmit = () => {
    return createStripeWithdrawal().then(close);
  };

  return (
    <div>
      <Typography component="h3" variant="title" fontWeight={400}>
        Add Stripe Account
      </Typography>
      <div className={style.addWithdrawalMethodCard}>
        <AddStripWithdrawalMethodForm onSubmit={handleSubmit} />
        {isForm && showResendEmail && (
          <Button
            variant="secondary"
            size="x-small"
            onClick={resendConfirmAddWithdrawalMethod}
          >
            Resend confirmation e-mail
          </Button>
        )}
      </div>
      <div className="buttons right mt+">
        <Button variant="transparent" color="secondary" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="transparent"
          color="primary"
          disabled={creatingWithdrawalMethod}
          onClick={onSubmit}
        >
          Add Stripe account
        </Button>
      </div>
    </div>
  );
};
